/**
 * CartContext - 장바구니 상태 관리
 *
 * 서버 우선 전략:
 * - 인증 사용자: 서버 API 호출 -> 응답으로 상태 업데이트 -> sessionStorage 캐시
 * - 비인증 사용자: 기존 클라이언트 사이드 방식 유지 (sessionStorage)
 * - 로그인 시: 클라이언트 장바구니를 서버로 마이그레이션
 * - 로그아웃 시: sessionStorage 비우기
 *
 * useReducer 기반 상태 전이:
 * - 명확한 액션 타입으로 상태 변경 추적
 * - useRef로 stale closure 해결
 * - 모든 mutation에 낙관적 업데이트 + 롤백 패턴 적용
 */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useReducer,
  type ReactNode,
} from 'react';
import type { CartItem } from '@/components/CartModal';
import { serverCartToClientItems } from '@/types/cart';
import { areCustomizationsEqual, extractMenuId } from '@/utils/cartUtils';
import { cartApi } from '@/lib/api/cart';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/AppContext';
import { cartTranslations } from '@/i18n/cartModal';
import { logger } from '@/utils/logger';
import { toast } from 'sonner';
import { currentComensalOrderBlockReason } from '@/lib/comensalAccess';
import { getComensalSessionLabels } from '@/i18n/comensalSession';
import {
  areInputValuesEqual,
  areSelectedOptionsEqual,
  normalizeInputValues,
  normalizeSelectedOptionsForPayload,
} from '@/utils/selectedOptions';

// 세션 스토리지 키
const CART_STORAGE_KEY = 'olulo_cart_items';

function sanitizeCartItem(item: CartItem): CartItem {
  return {
    ...item,
    selectedOptions: normalizeSelectedOptionsForPayload(item.selectedOptions),
  };
}

function sanitizeCartItems(items: CartItem[]): CartItem[] {
  return items.map(sanitizeCartItem);
}

function sanitizeCartItemUpdate(updates: Partial<CartItem>): Partial<CartItem> {
  if (!Object.prototype.hasOwnProperty.call(updates, 'selectedOptions')) {
    return updates;
  }

  return {
    ...updates,
    selectedOptions: normalizeSelectedOptionsForPayload(updates.selectedOptions),
  };
}

function hasStructuredOptionState(item: Pick<CartItem, 'selectedOptions' | 'inputValues'>): boolean {
  return Boolean(
    normalizeSelectedOptionsForPayload(item.selectedOptions) ||
    Object.keys(normalizeInputValues(item.inputValues)).length > 0,
  );
}

function areCartItemOptionsEqual(existingItem: CartItem, nextItem: Omit<CartItem, 'quantity'>): boolean {
  const existingHasStructuredState = hasStructuredOptionState(existingItem);
  const nextHasStructuredState = hasStructuredOptionState(nextItem);

  if (existingHasStructuredState && nextHasStructuredState) {
    return (
      areSelectedOptionsEqual(existingItem.selectedOptions, nextItem.selectedOptions) &&
      areInputValuesEqual(existingItem.inputValues, nextItem.inputValues)
    );
  }

  return areCustomizationsEqual(existingItem.customizations, nextItem.customizations);
}

/**
 * 브라우저 환경 체크 (SSR 호환성)
 */
function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

/**
 * 세션 스토리지에서 장바구니 아이템 불러오기
 */
function loadCartFromStorage(): CartItem[] {
  if (!isBrowser()) return [];

  try {
    const stored = sessionStorage.getItem(CART_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    logger.warn('장바구니 데이터 로드 실패:', error);
  }
  return [];
}

/**
 * 세션 스토리지에 장바구니 아이템 저장
 */
function saveCartToStorage(items: CartItem[]): void {
  if (!isBrowser()) return;

  try {
    sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    // QuotaExceededError 처리: 스토리지 용량 초과 시 기존 데이터 정리
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      logger.warn('sessionStorage 용량 초과, 스토리지 정리 중...');
      try {
        sessionStorage.removeItem(CART_STORAGE_KEY);
        sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
      } catch (retryError) {
        logger.error('장바구니 저장 실패 (정리 후에도 실패):', retryError);
      }
    } else {
      logger.warn('장바구니 데이터 저장 실패:', error);
    }
  }
}

// ==================== useReducer 타입 및 리듀서 ====================

type CartAction =
  | { type: 'SET_ITEMS'; items: CartItem[] }
  | { type: 'ADD_ITEM'; item: CartItem; existingIndex: number | null }
  | { type: 'UPDATE_QUANTITY'; itemId: string; quantity: number }
  | { type: 'UPDATE_ITEM'; itemId: string; updates: Partial<CartItem> }
  | { type: 'REMOVE_ITEM'; itemId: string }
  | { type: 'CLEAR' }
  | { type: 'SYNC_FROM_SERVER'; items: CartItem[] };

function cartReducer(state: CartItem[], action: CartAction): CartItem[] {
  switch (action.type) {
    case 'SET_ITEMS':
    case 'SYNC_FROM_SERVER':
      return sanitizeCartItems(action.items);
    case 'ADD_ITEM':
      if (action.existingIndex !== null) {
        return state.map((item, i) =>
          i === action.existingIndex
            ? { ...item, quantity: item.quantity + action.item.quantity }
            : item
        );
      }
      return [...state, sanitizeCartItem(action.item)];
    case 'UPDATE_QUANTITY':
      return state.map((item) =>
        item.id === action.itemId ? { ...item, quantity: action.quantity } : item
      );
    case 'UPDATE_ITEM': {
      const sanitizedUpdates = sanitizeCartItemUpdate(action.updates);
      return state.map((item) =>
        item.id === action.itemId ? sanitizeCartItem({ ...item, ...sanitizedUpdates }) : item
      );
    }
    case 'REMOVE_ITEM':
      return state.filter((item) => item.id !== action.itemId);
    case 'CLEAR':
      return [];
    default:
      return state;
  }
}

// ==================== Context 타입 및 Provider ====================

interface CartContextValue {
  // 상태
  cartItems: CartItem[];
  cartItemCount: number;
  cartTotal: number;
  /** 서버 동기화 진행 중 여부 */
  isSyncing: boolean;
  // 액션
  addToCart: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  removeFromCart: (itemId: string) => void;
  updateCartItem: (itemId: string, updatedItem: Partial<CartItem>) => void;
  clearCart: () => void;
  setCartItems: (items: CartItem[]) => void;
  /** 서버에서 장바구니 다시 불러오기 (인증 사용자만) */
  refreshFromServer: (storeId: number) => Promise<void>;
}

const CartContext = createContext<CartContextValue | null>(null);

interface CartProviderProps {
  children: ReactNode;
  initialItems?: CartItem[];
}

export function CartProvider({ children, initialItems = [] }: CartProviderProps) {
  const { isLoggedIn } = useAuth();
  const { locale } = useLanguage();
  const prevLoggedIn = useRef(isLoggedIn);

  // useReducer로 상태 전이 명확화
  const [cartItems, dispatch] = useReducer(cartReducer, [], () => {
    const storedItems = loadCartFromStorage();
    return sanitizeCartItems(storedItems.length > 0 ? storedItems : initialItems);
  });

  // useRef로 최신 상태 참조 (stale closure 해결)
  const cartItemsRef = useRef<CartItem[]>(cartItems);
  useEffect(() => {
    cartItemsRef.current = cartItems;
  }, [cartItems]);

  const [isSyncing, setIsSyncing] = useState(false);

  // 장바구니 변경 시 sessionStorage에 저장
  useEffect(() => {
    saveCartToStorage(cartItems);
  }, [cartItems]);

  /**
   * 클라이언트 장바구니를 서버로 마이그레이션
   * selected_options, special_request 포함 전송
   */
  const migrateCartToServer = useCallback(async (storeId: number, items: CartItem[]) => {
    setIsSyncing(true);
    try {
      for (const item of items) {
        const menuId = item.menuItemId ? Number(item.menuItemId) : null;
        if (menuId) {
          await cartApi.addItem(storeId, {
            menu_id: menuId,
            quantity: item.quantity,
            selected_options: normalizeSelectedOptionsForPayload(item.selectedOptions),
            // #502 (admin#1500): 입력형 옵션 사용자 입력 값 BE 전송 (cart 마이그레이션 흐름)
            input_values: item.inputValues || undefined,
            special_request: item.description || undefined,
          });
        }
      }
      // 마이그레이션 완료 후 서버에서 최신 데이터 가져오기
      const response = await cartApi.getCart(storeId);
      dispatch({ type: 'SYNC_FROM_SERVER', items: serverCartToClientItems(response.data) });
    } catch (error) {
      logger.warn('장바구니 서버 마이그레이션 실패, 클라이언트 데이터 유지:', error);
    } finally {
      setIsSyncing(false);
    }
  }, []);

  // 로그인 상태 변경 감지: 로그인 시 클라이언트 장바구니를 서버로 마이그레이션
  useEffect(() => {
    const wasLoggedIn = prevLoggedIn.current;
    prevLoggedIn.current = isLoggedIn;

    if (!wasLoggedIn && isLoggedIn && cartItemsRef.current.length > 0) {
      // 로그인 직후: 클라이언트 장바구니 아이템을 서버로 마이그레이션
      const storeId = cartItemsRef.current[0]?.restaurantId;
      if (storeId) {
        migrateCartToServer(Number(storeId), cartItemsRef.current);
      }
    }

    if (wasLoggedIn && !isLoggedIn) {
      // 로그아웃: sessionStorage 비우기
      dispatch({ type: 'CLEAR' });
    }
  }, [isLoggedIn, migrateCartToServer]);

  /**
   * 서버에서 장바구니 데이터 새로고침
   */
  const refreshFromServer = useCallback(async (storeId: number) => {
    if (!isLoggedIn) return;

    setIsSyncing(true);
    try {
      const response = await cartApi.getCart(storeId);
      dispatch({ type: 'SYNC_FROM_SERVER', items: serverCartToClientItems(response.data) });
    } catch (error) {
      logger.warn('서버 장바구니 조회 실패:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [isLoggedIn]);

  // 장바구니에 아이템 추가
  // 동일한 메뉴 + 동일한 옵션인 경우 수량만 증가 (#14 버그 수정)
  // 낙관적 업데이트 + 롤백 패턴 적용
  const addToCart = useCallback((item: Omit<CartItem, 'quantity'>, quantity: number = 1) => {
    const orderBlockReason = currentComensalOrderBlockReason();
    if (orderBlockReason !== null) {
      const labels = getComensalSessionLabels(locale);
      toast.error(orderBlockReason === 'split_disabled_with_ledger'
        ? labels.ledgerCartBlocked
        : labels.cartBlocked);
      return;
    }

    const previousItems = cartItemsRef.current;
    const normalizedItem: Omit<CartItem, 'quantity'> = {
      ...item,
      selectedOptions: normalizeSelectedOptionsForPayload(item.selectedOptions),
    };

    // 다른 매장의 메뉴 추가 시 확인 다이얼로그
    let storeCleared = false;
    const currentStoreId = previousItems[0]?.restaurantId;
    const newStoreId = normalizedItem.restaurantId;
    if (currentStoreId && newStoreId && currentStoreId !== newStoreId && previousItems.length > 0) {
      const t = cartTranslations[locale] || cartTranslations.en;
      const confirmed = window.confirm(t.differentStoreConfirm);
      if (!confirmed) return;
      // 장바구니 초기화 + 인증 사용자는 서버 장바구니도 비우기
      const prevStoreId = previousItems[0]?.restaurantId;
      dispatch({ type: 'CLEAR' });
      storeCleared = true;
      if (isLoggedIn && prevStoreId) {
        cartApi.clearCart(Number(prevStoreId)).catch((error) => {
          logger.warn('매장 전환 시 서버 장바구니 비우기 실패:', error);
        });
      }
    }

    // 메뉴 ID 추출 (타임스탬프 제거)
    const newItemMenuId = normalizedItem.menuItemId || extractMenuId(normalizedItem.id);

    // 동일한 메뉴 + 동일한 옵션을 가진 기존 아이템 찾기
    // CLEAR 직후에는 장바구니가 비었으므로 중복 검사 스킵
    let existingIndex = -1;
    if (!storeCleared) {
      existingIndex = previousItems.findIndex((cartItem) => {
        const existingMenuId = cartItem.menuItemId || extractMenuId(cartItem.id);
        if (existingMenuId !== newItemMenuId) return false;
        return areCartItemOptionsEqual(cartItem, normalizedItem);
      });
    }

    // 낙관적 업데이트
    dispatch({
      type: 'ADD_ITEM',
      item: { ...normalizedItem, quantity },
      existingIndex: existingIndex !== -1 ? existingIndex : null,
    });

    // 인증 사용자: 서버에 동기화
    if (isLoggedIn) {
      const menuId = normalizedItem.menuItemId ? Number(normalizedItem.menuItemId) : null;
      const storeId = normalizedItem.restaurantId ? Number(normalizedItem.restaurantId) : null;
      if (menuId && storeId) {
        cartApi.addItem(storeId, {
          menu_id: menuId,
          quantity,
          selected_options: normalizedItem.selectedOptions,
          // #502 (admin#1500): 입력형 옵션 사용자 입력 값 BE 전송
          input_values: normalizedItem.inputValues || undefined,
        }).then((response) => {
          dispatch({ type: 'SYNC_FROM_SERVER', items: serverCartToClientItems(response.data) });
        }).catch((error) => {
          dispatch({ type: 'SET_ITEMS', items: previousItems }); // 롤백
          logger.warn('서버 장바구니 추가 실패, 이전 상태 복원:', error);
        });
      }
    }
  }, [isLoggedIn, locale]);

  // 수량 업데이트 (낙관적 업데이트 + 롤백)
  const updateQuantity = useCallback((itemId: string, quantity: number) => {
    const previousItems = cartItemsRef.current;

    // 낙관적 업데이트
    dispatch({ type: 'UPDATE_QUANTITY', itemId, quantity });

    // 인증 사용자: 서버 동기화
    if (isLoggedIn) {
      const targetItem = previousItems.find((i) => i.id === itemId);
      if (targetItem?.serverCartItemId && targetItem.restaurantId) {
        cartApi.updateItem(
          Number(targetItem.restaurantId),
          targetItem.serverCartItemId,
          { quantity }
        ).then((response) => {
          dispatch({ type: 'SYNC_FROM_SERVER', items: serverCartToClientItems(response.data) });
        }).catch((error) => {
          dispatch({ type: 'SET_ITEMS', items: previousItems }); // 롤백
          logger.warn('서버 장바구니 수량 업데이트 실패, 이전 상태 복원:', error);
        });
      }
    }
  }, [isLoggedIn]);

  // 아이템 제거 (낙관적 업데이트 + 롤백)
  const removeFromCart = useCallback((itemId: string) => {
    const previousItems = cartItemsRef.current;
    const targetItem = previousItems.find((i) => i.id === itemId);

    // 낙관적 업데이트
    dispatch({ type: 'REMOVE_ITEM', itemId });

    // 인증 사용자: 서버 동기화
    if (isLoggedIn && targetItem?.serverCartItemId && targetItem.restaurantId) {
      cartApi.removeItem(
        Number(targetItem.restaurantId),
        targetItem.serverCartItemId
      ).then((response) => {
        dispatch({ type: 'SYNC_FROM_SERVER', items: serverCartToClientItems(response.data) });
      }).catch((error) => {
        dispatch({ type: 'SET_ITEMS', items: previousItems }); // 롤백
        logger.warn('서버 장바구니 아이템 삭제 실패, 이전 상태 복원:', error);
      });
    }
  }, [isLoggedIn]);

  // 아이템 부분 업데이트
  const updateCartItem = useCallback((itemId: string, updatedItem: Partial<CartItem>) => {
    dispatch({ type: 'UPDATE_ITEM', itemId, updates: updatedItem });
  }, []);

  // 장바구니 비우기 (낙관적 업데이트 + 롤백)
  const clearCart = useCallback(() => {
    const previousItems = cartItemsRef.current;

    // 낙관적 업데이트 먼저 (다른 함수들과 동일한 순서)
    dispatch({ type: 'CLEAR' });

    // 인증 사용자: 서버 장바구니도 비우기
    if (isLoggedIn) {
      const storeId = previousItems[0]?.restaurantId;
      if (storeId) {
        cartApi.clearCart(Number(storeId)).catch((error) => {
          dispatch({ type: 'SET_ITEMS', items: previousItems }); // 롤백
          logger.warn('서버 장바구니 비우기 실패, 이전 상태 복원:', error);
        });
      }
    }
  }, [isLoggedIn]);

  // 장바구니 아이템 직접 설정
  const setCartItems = useCallback((items: CartItem[]) => {
    dispatch({ type: 'SET_ITEMS', items });
  }, []);

  // 계산된 값
  const cartItemCount = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems]
  );

  const cartTotal = useMemo(
    () => {
      const total = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
      // 부동소수점 연산 오차 방지: 소수점 2자리로 반올림
      return Math.round(total * 100) / 100;
    },
    [cartItems]
  );

  // Context 값 메모이제이션
  const value = useMemo<CartContextValue>(
    () => ({
      cartItems,
      cartItemCount,
      cartTotal,
      isSyncing,
      addToCart,
      updateQuantity,
      removeFromCart,
      updateCartItem,
      clearCart,
      setCartItems,
      refreshFromServer,
    }),
    [
      cartItems,
      cartItemCount,
      cartTotal,
      isSyncing,
      addToCart,
      updateQuantity,
      removeFromCart,
      updateCartItem,
      clearCart,
      setCartItems,
      refreshFromServer,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

/**
 * useCart - CartContext 사용 훅
 *
 * @throws CartProvider 외부에서 사용 시 에러
 */
export function useCart(): CartContextValue {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

/**
 * useCartItems - 장바구니 아이템만 사용
 */
export function useCartItems() {
  const { cartItems, cartItemCount, cartTotal } = useCart();
  return { cartItems, cartItemCount, cartTotal };
}

/**
 * useCartActions - 장바구니 액션만 사용
 */
export function useCartActions() {
  const { addToCart, updateQuantity, removeFromCart, updateCartItem, clearCart, setCartItems } =
    useCart();
  return { addToCart, updateQuantity, removeFromCart, updateCartItem, clearCart, setCartItems };
}
